import { Slot } from '@radix-ui/react-slot';
import type { ComponentPropsWithoutRef, ComponentRef, HTMLAttributes, Ref } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

type ButtonGroupContextValue = {
  orientation: 'horizontal' | 'vertical';
  attached: boolean;
};

const ButtonGroupContext = createContext<ButtonGroupContextValue>({
  orientation: 'horizontal',
  attached: true,
});

function useButtonGroupContext(): ButtonGroupContextValue {
  return useContext(ButtonGroupContext);
}

const HORIZONTAL_ATTACHED_CLASSES = [
  'isolate flex-row',
  '[&>[data-slot=button]]:rounded-none',
  '[&>[data-slot=button]:first-child]:rounded-l-md',
  '[&>[data-slot=button]:last-child]:rounded-r-md',
  '[&>[data-slot=button]:only-child]:rounded-md',
  '[&>[data-slot=button]:not(:first-child)]:-ml-px',
  '[&>[data-slot=button-group-text]]:rounded-none',
  '[&>[data-slot=button-group-text]:first-child]:rounded-l-md',
  '[&>[data-slot=button-group-text]:last-child]:rounded-r-md',
  '[&>[data-slot=button-group-text]:only-child]:rounded-md',
  '[&>[data-slot=button-group-text]:not(:first-child)]:-ml-px',
];

const VERTICAL_ATTACHED_CLASSES = [
  'isolate flex-col',
  '[&>[data-slot=button]]:rounded-none',
  '[&>[data-slot=button]:first-child]:rounded-t-md',
  '[&>[data-slot=button]:last-child]:rounded-b-md',
  '[&>[data-slot=button]:only-child]:rounded-md',
  '[&>[data-slot=button]:not(:first-child)]:-mt-px',
  '[&>[data-slot=button-group-text]]:rounded-none',
  '[&>[data-slot=button-group-text]:first-child]:rounded-t-md',
  '[&>[data-slot=button-group-text]:last-child]:rounded-b-md',
  '[&>[data-slot=button-group-text]:only-child]:rounded-md',
  '[&>[data-slot=button-group-text]:not(:first-child)]:-mt-px',
];

export interface ButtonGroupProperties extends HTMLAttributes<HTMLDivElement> {
  attached?: boolean;
  orientation?: 'horizontal' | 'vertical';
  ref?: Ref<HTMLDivElement> | null;
}

export const ButtonGroup = ({
  className,
  orientation = 'horizontal',
  attached = true,
  children,
  ref,
  ...properties
}: ButtonGroupProperties) => {
  const value = useMemo<ButtonGroupContextValue>(() => ({ orientation, attached }), [orientation, attached]);

  let attachedClasses: string | readonly string[];
  if (attached) {
    attachedClasses = orientation === 'horizontal' ? HORIZONTAL_ATTACHED_CLASSES : VERTICAL_ATTACHED_CLASSES;
  } else {
    attachedClasses = orientation === 'horizontal' ? 'flex-row' : 'flex-col';
  }
  const resolvedRef = ref ?? null;

  return (
    <ButtonGroupContext.Provider value={value}>
      {/* biome-ignore lint/a11y/useSemanticElements: Button groups use role="group" for screen readers. */}
      <div
        ref={resolvedRef}
        role="group"
        data-orientation={orientation}
        data-attached={attached ? '' : undefined}
        className={cn(
          'inline-flex items-center rounded-md shadow-sm',
          attached ? 'gap-0' : 'gap-2',
          attachedClasses,
          className
        )}
        {...properties}
      >
        {children}
      </div>
    </ButtonGroupContext.Provider>
  );
};

export type ButtonGroupSeparatorProps = ComponentPropsWithoutRef<typeof Separator> & {
  ref?: Ref<ComponentRef<typeof Separator>> | null;
};

export const ButtonGroupSeparator = ({
  className,
  orientation: overrideOrientation,
  decorative = true,
  ref,
  ...properties
}: ButtonGroupSeparatorProps) => {
  const { orientation } = useButtonGroupContext();
  const separatorOrientation = overrideOrientation ?? (orientation === 'horizontal' ? 'vertical' : 'horizontal');
  const resolvedRef = ref ?? null;

  return (
    <Separator
      ref={resolvedRef}
      orientation={separatorOrientation}
      decorative={decorative}
      className={cn(
        orientation === 'horizontal' ? 'mx-0 my-1 h-6 w-px self-stretch' : 'mx-1 my-0 w-full self-stretch',
        className
      )}
      {...properties}
    />
  );
};

export interface ButtonGroupTextProperties extends HTMLAttributes<HTMLElement> {
  asChild?: boolean;
  ref?: Ref<HTMLElement> | null;
}

export const ButtonGroupText = ({ className, asChild = false, ref, ...properties }: ButtonGroupTextProperties) => {
  const { orientation, attached } = useButtonGroupContext();

  const attachedStyles: Record<ButtonGroupContextValue['orientation'], string> = {
    horizontal: 'border border-border/70 bg-card [&:not(:first-child)]:-ml-px',
    vertical: 'border border-border/70 bg-card [&:not(:first-child)]:-mt-px',
  };
  const attachmentClasses = attached ? attachedStyles[orientation] : 'border border-transparent';

  const resolvedRef = ref ?? null;

  const commonProps = {
    ref: resolvedRef,
    'data-slot': 'button-group-text',
    className: cn(
      'inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-muted-foreground',
      attachmentClasses,
      className
    ),
    ...properties,
  };

  if (asChild) {
    return <Slot {...commonProps} />;
  }

  return <span {...commonProps} />;
};
